/*
  ZF wholesale program page (sections/zf-wholesale.liquid). Vanilla JS, loaded with defer.

  What it does:
    - Remembers in this browser that an application was submitted (a timestamp only, no personal data),
      so a returning applicant sees the "submitted" screen with a link to apply again.
    - Validates the built-in application form and shows inline errors next to each field.
    - Keeps what was typed for the length of the tab session, so nothing is lost if Shopify sends the
      applicant back with an error. The copy is removed as soon as the application goes through.

  What it does not do: no cart requests, no pricing, no quantity rules, no approval logic.
  Those live in Shopify admin. The page works without this file: the form posts normally and
  Shopify's own redirect shows the submitted screen.
*/
(function () {
  'use strict';

  var REMEMBER_KEY = 'zfWholesaleApplied';
  var DRAFT_KEY = 'zfWholesaleDraft';
  var DAY_MS = 86400000;
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function getStore(name) {
    try {
      var store = window[name];
      var probe = '__zf_ws_probe__';
      store.setItem(probe, '1');
      store.removeItem(probe);
      return store;
    } catch (error) {
      return null;
    }
  }

  function readJSON(store, key) {
    if (!store) return null;
    try {
      return JSON.parse(store.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  }

  function writeJSON(store, key, value) {
    if (!store) return;
    try {
      store.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* storage full or blocked: the page still works without it */
    }
  }

  function removeKey(store, key) {
    if (!store) return;
    try {
      store.removeItem(key);
    } catch (error) {
      /* nothing to clean up */
    }
  }

  function focusElement(element) {
    if (!element) return;
    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (element.scrollIntoView) {
      element.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    }
  }

  /* Shopify's redirect adds ?contact_posted=true and a #hash. Dropping them keeps a reload from re-running this state. */
  function cleanUrl() {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('contact_posted');
      url.searchParams.delete('application');
      url.hash = '';
      window.history.replaceState(window.history.state, '', url.pathname + url.search);
    } catch (error) {
      /* older browsers keep the original address */
    }
  }

  function urlSaysReceived() {
    return /[?&]application=received(&|$)/.test(window.location.search) || window.location.hash === '#application-received';
  }

  function fieldMessage(field) {
    var type = (field.getAttribute('type') || '').toLowerCase();
    var value = type === 'checkbox' ? '' : String(field.value || '').trim();

    if (field.required) {
      var missing = type === 'checkbox' ? !field.checked : value === '';
      if (missing) return field.getAttribute('data-msg-required') || 'This field is required.';
    }
    if (value !== '' && type === 'email' && !EMAIL_PATTERN.test(value)) {
      return field.getAttribute('data-msg-invalid') || 'Enter a valid email address.';
    }
    if (value !== '' && type === 'tel' && value.replace(/\D/g, '').length < 7) {
      return field.getAttribute('data-msg-invalid') || 'Enter a valid phone number.';
    }
    return '';
  }

  function showFieldMessage(field, message) {
    var slot = document.getElementById(field.id + '-error');
    if (message) {
      field.setAttribute('aria-invalid', 'true');
      if (slot) {
        slot.textContent = message;
        slot.hidden = false;
      }
    } else {
      field.removeAttribute('aria-invalid');
      if (slot) {
        slot.textContent = '';
        slot.hidden = true;
      }
    }
  }

  function formFields(form) {
    return Array.prototype.filter.call(form.elements, function (element) {
      return element.name && element.type !== 'hidden' && element.type !== 'submit' && element.type !== 'button';
    });
  }

  function saveDraft(form, sessionStore) {
    var draft = {};
    formFields(form).forEach(function (field) {
      /* The registration or tax ID is never written to browser storage. */
      if (/registration|tax/i.test(field.name || '')) return;
      draft[field.name] = field.type === 'checkbox' ? field.checked : field.value;
    });
    writeJSON(sessionStore, DRAFT_KEY, draft);
  }

  function restoreDraft(form, sessionStore) {
    var draft = readJSON(sessionStore, DRAFT_KEY);
    if (!draft) return;
    formFields(form).forEach(function (field) {
      if (!Object.prototype.hasOwnProperty.call(draft, field.name)) return;
      if (field.type === 'checkbox') {
        field.checked = draft[field.name] === true;
      } else if (typeof draft[field.name] === 'string') {
        field.value = draft[field.name];
      }
    });
  }

  function initForm(root, form, sessionStore) {
    var summary = form.querySelector('[data-zf-ws-summary]');
    var serverErrors = form.querySelector('[data-zf-ws-server-errors]');
    var submitButton = form.querySelector('[data-zf-ws-submit]');
    var submitLabel = submitButton ? submitButton.textContent : '';
    var sending = false;

    /* Inline errors replace the browser's own bubbles. Without this script the browser validates natively. */
    form.setAttribute('novalidate', 'novalidate');

    /* WebMCP declarative annotations. No toolautosubmit: the applicant always presses the button. */
    if (root.dataset.toolName && root.dataset.toolDescription) {
      form.setAttribute('toolname', root.dataset.toolName);
      form.setAttribute('tooldescription', root.dataset.toolDescription);
    }

    if (serverErrors) {
      restoreDraft(form, sessionStore);
      removeKey(sessionStore, DRAFT_KEY);
      focusElement(serverErrors);
    }

    function resetSubmit() {
      sending = false;
      if (!submitButton) return;
      submitButton.removeAttribute('aria-disabled');
      submitButton.textContent = submitLabel;
    }

    form.addEventListener('submit', function (event) {
      if (sending) {
        event.preventDefault();
        return;
      }

      var firstInvalid = null;
      formFields(form).forEach(function (field) {
        var message = fieldMessage(field);
        showFieldMessage(field, message);
        if (message && !firstInvalid) firstInvalid = field;
      });

      if (firstInvalid) {
        event.preventDefault();
        if (summary) summary.hidden = false;
        focusElement(firstInvalid);
        return;
      }

      if (summary) summary.hidden = true;
      saveDraft(form, sessionStore);
      sending = true;
      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.textContent = submitButton.getAttribute('data-label-busy') || submitLabel;
      }
      /* If the request never leaves (offline, blocked), give the button back after a while. */
      window.setTimeout(resetSubmit, 20000);
    });

    function recheck(event) {
      var field = event.target;
      if (!field || !field.name || field.getAttribute('aria-invalid') !== 'true') return;
      showFieldMessage(field, fieldMessage(field));
      if (summary && !form.querySelector('[aria-invalid="true"]')) summary.hidden = true;
    }

    form.addEventListener('input', recheck);
    form.addEventListener('change', recheck);

    /* Coming back with the Back button restores the page from cache with the button still marked busy. */
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) resetSubmit();
    });
  }

  function init(root) {
    if (!root || root.dataset.zfWsReady === 'true') return;
    root.dataset.zfWsReady = 'true';

    var localStore = getStore('localStorage');
    var sessionStore = getStore('sessionStorage');
    var state = root.dataset.state;
    var inEditor = root.dataset.designMode === 'true';

    /* Once the owner has acted on an application, this browser's reminder is no longer needed. */
    if (state !== 'apply') {
      if (!inEditor) {
        removeKey(localStore, REMEMBER_KEY);
        removeKey(sessionStore, DRAFT_KEY);
      }
      return;
    }

    var rememberDays = parseInt(root.dataset.rememberDays, 10) || 30;
    var submittedTitle = root.querySelector('[data-zf-ws-submitted-title]');
    var formTitle = root.querySelector('[data-zf-ws-form-title]');
    var againLink = root.querySelector('[data-zf-ws-again]');
    var form = root.querySelector('form.zf-ws__form');

    function showSubmitted(moveFocus) {
      root.setAttribute('data-view', 'submitted');
      if (moveFocus) focusElement(submittedTitle);
    }

    function showForm(moveFocus) {
      root.setAttribute('data-view', 'form');
      if (moveFocus) focusElement(formTitle);
    }

    function remember() {
      writeJSON(localStore, REMEMBER_KEY, { t: Date.now() });
    }

    function receivedFromUrl() {
      remember();
      cleanUrl();
      showSubmitted(true);
    }

    if (form) initForm(root, form, sessionStore);

    if (!inEditor) {
      if (root.dataset.posted === 'true') {
        remember();
        removeKey(sessionStore, DRAFT_KEY);
        cleanUrl();
        showSubmitted(true);
      } else if (urlSaysReceived()) {
        /* A Shopify Forms block can redirect here with ?application=received after a submit. */
        receivedFromUrl();
      } else {
        var remembered = readJSON(localStore, REMEMBER_KEY);
        var fresh = remembered && remembered.t && Date.now() - remembered.t < rememberDays * DAY_MS;
        if (fresh) {
          showSubmitted(false);
        } else {
          removeKey(localStore, REMEMBER_KEY);
          showForm(false);
        }
      }

      window.addEventListener('hashchange', function () {
        if (window.location.hash === '#application-received') receivedFromUrl();
      });
    }

    if (againLink) {
      againLink.addEventListener('click', function (event) {
        event.preventDefault();
        removeKey(localStore, REMEMBER_KEY);
        cleanUrl();
        showForm(true);
      });
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-zf-wholesale]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme editor re-renders the section when a setting changes. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
